import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { download } from '../plan/lib/api';
import type { ReleaseCandidates, ReleaseDetail, ReleaseLine, ReleaseSummary } from '../lib/types';
import { DateCell } from '../components/DateCell';
import { Modal } from '../components/Modal';
import { useAsk } from '../components/Ask';
import { TaskDrawer } from '../components/TaskDrawer';
import { formatDate, todayValue } from '../lib/dates';
import { Linkified } from '../components/Linkified';

/**
 * RELEASES — what went live, written down and sent.
 *
 * Work goes live from several projects at once, often for several companies, and the record
 * of it used to be an email somebody wrote from memory. Here it is put together from what was
 * actually finished, saved, downloaded as a PDF, and emailed from the Hub — so there is one
 * copy of what was said, and a list of who it went to.
 *
 * Three screens on one route family: the list, one release, and the editor for a new or
 * existing one.
 */
export function ReleasesPage() {
  const { releaseId, mode } = useParams<{ releaseId?: string; mode?: string }>();
  if (releaseId === 'new') return <ReleaseEditor />;
  if (releaseId && mode === 'edit') return <ReleaseEditor releaseId={releaseId} />;
  if (releaseId) return <ReleaseView releaseId={releaseId} />;
  return <ReleaseList />;
}

function ReleaseList() {
  const [releases, setReleases] = useState<ReleaseSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<ReleaseSummary[]>('/releases')
      .then(setReleases)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load releases'));
  }, []);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Releases</h1>
          <p className="subtitle">What went live, across projects — saved, and sent to whoever needs to know.</p>
        </div>
        <div className="row">
          <Link to="/releases/new" className="btn btn-primary">
            New release
          </Link>
        </div>
      </div>

      {error ? (
        <div className="callout">{error}</div>
      ) : !releases ? (
        <div className="empty">Loading…</div>
      ) : releases.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>No releases recorded yet</h3>
            <p>When work goes live, put it together here — pick the projects and tasks, then save and email it.</p>
            <Link to="/releases/new" className="btn btn-primary">
              Record the first one
            </Link>
          </div>
        </div>
      ) : (
        <div className="card">
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Went live</th>
                <th>Release</th>
                <th>Companies</th>
                <th className="right">Projects</th>
                <th className="right">Tasks</th>
                <th>Sent</th>
              </tr>
            </thead>
            <tbody>
              {releases.map((release) => (
                <tr key={release.id}>
                  <td className="mono">{formatDate(release.releasedOn)}</td>
                  <td>
                    <Link to={`/releases/${release.id}`}>
                      <strong>{release.title}</strong>
                    </Link>
                    {release.createdBy && (
                      <div className="dim" style={{ fontSize: 11 }}>
                        by {release.createdBy}
                      </div>
                    )}
                  </td>
                  <td className="muted">{release.companies.join(', ') || '—'}</td>
                  <td className="right mono">{release.projects}</td>
                  <td className="right mono">{release.tasks}</td>
                  <td>
                    {release.lastSent ? (
                      <span className="dim" style={{ fontSize: 12 }} title={release.lastSent.to.join(', ')}>
                        {formatDate(release.lastSent.at.slice(0, 10))} to {release.lastSent.to.length}
                      </span>
                    ) : (
                      <span className="badge">not sent</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Lines as a reader takes them in: by company, then project, then its tasks. */
type ShownLine = Pick<ReleaseLine, 'companyName' | 'projectName' | 'projectId' | 'workItemId' | 'workItemKey' | 'workItemTitle' | 'note'>;

function grouped(lines: ShownLine[]) {
  const companies: Array<{
    name: string;
    projects: Array<{ name: string; projectId: string | null; note: string; tasks: ShownLine[] }>;
  }> = [];
  for (const line of lines) {
    let company = companies.find((entry) => entry.name === line.companyName);
    if (!company) companies.push((company = { name: line.companyName, projects: [] }));
    let project = company.projects.find((entry) => entry.name === line.projectName);
    if (!project) company.projects.push((project = { name: line.projectName, projectId: line.projectId, note: '', tasks: [] }));
    if (line.workItemKey) project.tasks.push(line);
    else if (line.note) project.note = project.note ? `${project.note}\n${line.note}` : line.note;
  }
  return companies;
}

function ReleaseView({ releaseId }: { releaseId: string }) {
  const navigate = useNavigate();
  const ask = useAsk();
  const [release, setRelease] = useState<ReleaseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /** The task open over the release, if any — the same drawer as everywhere else in the Hub. */
  const [openTask, setOpenTask] = useState<{ id: string; projectId: string } | null>(null);

  const load = useCallback(() => {
    api
      .get<ReleaseDetail>(`/releases/${releaseId}`)
      .then(setRelease)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load that release'));
  }, [releaseId]);

  useEffect(load, [load]);

  if (error) return <div className="callout">{error}</div>;
  if (!release) return <div className="empty">Loading…</div>;

  async function remove() {
    if (!release) return;
    const sure = await ask.confirm({
      title: `Delete “${release.title}”?`,
      body: release.sends.length
        ? `It has been emailed ${release.sends.length} time${release.sends.length === 1 ? '' : 's'}. Deleting it here does not unsend those.`
        : 'It has not been emailed to anybody.',
      confirmLabel: 'Delete release',
      danger: true,
    });
    if (!sure) return;
    try {
      await api.del(`/releases/${release.id}`);
      navigate('/releases');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete it');
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">
            <Link to="/releases" className="muted">
              Releases
            </Link>
          </div>
          <h1>{release.title}</h1>
          <p className="subtitle">
            Went live on {formatDate(release.releasedOn)}
            {release.createdBy ? ` · recorded by ${release.createdBy}` : ''}
          </p>
        </div>
        <div className="row">
          <button
            type="button"
            className="btn"
            onClick={() =>
              void download(`/releases/${release.id}/report.pdf`).catch((err) =>
                setError(err instanceof ApiError ? err.message : 'Could not download it'),
              )
            }
          >
            Download PDF
          </button>
          <Link to={`/releases/${release.id}/edit`} className="btn">
            Edit
          </Link>
          <button type="button" className="btn btn-primary" onClick={() => setSending(true)}>
            Email it
          </button>
        </div>
      </div>

      {notice && <div className="callout callout-ok mb-2">{notice}</div>}
      {release.hiddenLines > 0 && (
        <div className="callout mb-2">
          {release.hiddenLines} line{release.hiddenLines === 1 ? ' is' : 's are'} on projects you cannot see, so{' '}
          {release.hiddenLines === 1 ? 'it is' : 'they are'} not shown here.
        </div>
      )}

      <div className="card card-pad">
        {release.summary && <p style={{ marginTop: 0, whiteSpace: 'pre-wrap' }}><Linkified text={release.summary} /></p>}
        <ReleaseBody lines={release.items} onOpenTask={setOpenTask} />
      </div>

      <div className="card mt-2">
        <div className="card-head">
          <h2>Sent</h2>
          <span className="dim" style={{ fontSize: 12 }}>
            {release.sends.length === 0 ? 'Not emailed yet' : `${release.sends.length} time${release.sends.length === 1 ? '' : 's'}`}
          </span>
        </div>
        {release.sends.length > 0 && (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 160 }}>When</th>
                <th>By</th>
                <th>To</th>
              </tr>
            </thead>
            <tbody>
              {[...release.sends].reverse().map((send) => (
                <tr key={send.at}>
                  <td className="mono">
                    {formatDate(send.at.slice(0, 10))} {new Date(send.at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td>{send.byName}</td>
                  <td className="muted">{send.to.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {release.mayDelete && (
        <div className="mt-2">
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => void remove()}>
            Delete this release
          </button>
        </div>
      )}

      {openTask && <TaskDrawer itemId={openTask.id} projectId={openTask.projectId} onClose={() => setOpenTask(null)} />}

      {sending && (
        <SendModal
          release={release}
          onClose={() => setSending(false)}
          onSent={(to) => {
            setSending(false);
            setNotice(`Sent to ${to.join(', ')}, with the PDF attached.`);
            load();
          }}
        />
      )}
    </>
  );
}

/**
 * The release as it reads. A task opens in the drawer over the page, so reading what went out
 * and checking the work behind it is one click and no lost place; a project name goes to its
 * board. A line whose task or project has since been deleted still reads, and opens nothing.
 */
function ReleaseBody({
  lines,
  onOpenTask,
}: {
  lines: ShownLine[];
  onOpenTask: (task: { id: string; projectId: string }) => void;
}) {
  if (lines.length === 0) return <p className="dim">Nothing on it yet.</p>;
  return (
    <div className="stack-sm">
      {grouped(lines).map((company) => (
        <div key={company.name}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>
            {company.name}
          </div>
          {company.projects.map((project) => (
            <div key={project.name} className="release-project">
              {project.projectId ? (
                <Link to={`/projects/${project.projectId}/board`} title="Open this project's board">
                  <strong>{project.name}</strong>
                </Link>
              ) : (
                <strong>{project.name}</strong>
              )}
              {project.note && <div className="muted" style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}><Linkified text={project.note} /></div>}
              {project.tasks.length > 0 && (
                <ul className="release-tasks">
                  {project.tasks.map((task) => (
                    <li key={task.workItemKey}>
                      {task.workItemId && task.projectId ? (
                        <button
                          type="button"
                          className="link-button release-task"
                          title={`Open ${task.workItemKey} — ${task.workItemTitle}`}
                          onClick={() => onOpenTask({ id: task.workItemId!, projectId: task.projectId! })}
                        >
                          <span className="key">{task.workItemKey}</span> {task.workItemTitle}
                        </button>
                      ) : (
                        <span title="This task has since been deleted">
                          <span className="key">{task.workItemKey}</span> {task.workItemTitle}
                        </span>
                      )}
                      {task.note && <div className="muted" style={{ fontSize: 12 }}><Linkified text={task.note} /></div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function SendModal({ release, onClose, onSent }: { release: ReleaseDetail; onClose: () => void; onSent: (to: string[]) => void }) {
  const [to, setTo] = useState(release.sends.at(-1)?.to.join(', ') ?? '');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addresses = to
    .split(/[\s,;]+/)
    .map((value) => value.trim())
    .filter(Boolean);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/releases/${release.id}/send`, { to: addresses, message: message.trim() || undefined });
      onSent(addresses);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send it');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Email this release"
      subtitle="The report goes in the email itself, with the same thing attached as a PDF."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || addresses.length === 0} onClick={() => void send()}>
            {busy ? 'Sending…' : `Send to ${addresses.length || '…'}`}
          </button>
        </>
      }
    >
      {error && <div className="callout mb-2">{error}</div>}
      <label className="field">
        <span className="field-label">To</span>
        <textarea
          rows={2}
          value={to}
          placeholder="name@company.co.za, another@company.co.za"
          onChange={(event) => setTo(event.target.value)}
        />
        <span className="dim" style={{ fontSize: 11 }}>
          Separate addresses with commas or spaces. Everybody named gets one email, and can see who else has it.
        </span>
      </label>
      <label className="field">
        <span className="field-label">A note above the report (optional)</span>
        <textarea rows={3} value={message} onChange={(event) => setMessage(event.target.value)} />
      </label>
    </Modal>
  );
}

type DraftLine = Omit<ReleaseLine, 'id'>;

function shift(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function ReleaseEditor({ releaseId }: { releaseId?: string }) {
  const navigate = useNavigate();
  const today = todayValue();
  const [title, setTitle] = useState('');
  const [releasedOn, setReleasedOn] = useState(today);
  const [summary, setSummary] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [from, setFrom] = useState(shift(today, -14));
  const [to, setTo] = useState(today);
  const [candidates, setCandidates] = useState<ReleaseCandidates | null>(null);
  const [anyProjects, setAnyProjects] = useState<Array<{ value: string; label: string; hint?: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(!releaseId);

  useEffect(() => {
    if (!releaseId) return;
    api
      .get<ReleaseDetail>(`/releases/${releaseId}`)
      .then((release) => {
        setTitle(release.title);
        setReleasedOn(release.releasedOn);
        setSummary(release.summary);
        setLines(release.items.map(({ id: _id, ...line }) => line));
        setLoaded(true);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load that release'));
  }, [releaseId]);

  useEffect(() => {
    api
      .get<ReleaseCandidates>(`/releases/candidates?from=${from}&to=${to}`)
      .then(setCandidates)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load what was finished'));
  }, [from, to]);

  useEffect(() => {
    api
      .get<Array<{ value: string; label: string; hint?: string }>>('/plan/project-options')
      .then(setAnyProjects)
      .catch(() => setAnyProjects([]));
  }, []);

  const has = (projectId: string, workItemId: string | null) =>
    lines.some((line) => line.projectId === projectId && line.workItemId === workItemId);

  function toggle(line: DraftLine) {
    setLines((current) =>
      has(line.projectId!, line.workItemId)
        ? current.filter((entry) => !(entry.projectId === line.projectId && entry.workItemId === line.workItemId))
        : [...current, line],
    );
  }

  const companyOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of candidates?.projects ?? []) map.set(entry.project.id, entry.company.name);
    return map;
  }, [candidates]);

  async function save() {
    setBusy(true);
    setError(null);
    const body = {
      title: title.trim(),
      releasedOn,
      summary: summary.trim(),
      items: lines.map((line) => ({
        projectId: line.projectId,
        workItemId: line.workItemId ?? undefined,
        note: line.note.trim() || undefined,
      })),
    };
    try {
      const saved = releaseId
        ? await api.put<ReleaseDetail>(`/releases/${releaseId}`, body)
        : await api.post<ReleaseDetail>('/releases', body);
      navigate(`/releases/${saved.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save it');
    } finally {
      setBusy(false);
    }
  }

  if (!loaded && !error) return <div className="empty">Loading…</div>;


  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">
            <Link to="/releases" className="muted">
              Releases
            </Link>
          </div>
          <h1>{releaseId ? 'Edit the release' : 'New release'}</h1>
          <p className="subtitle">Tick what went live, add a word where it helps, and save. You can email it after.</p>
        </div>
        <div className="row">
          <Link to={releaseId ? `/releases/${releaseId}` : '/releases'} className="btn btn-quiet">
            Cancel
          </Link>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || title.trim().length < 2 || lines.length === 0}
            title={lines.length === 0 ? 'Tick at least one project or task' : undefined}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save release'}
          </button>
        </div>
      </div>

      {error && <div className="callout mb-2">{error}</div>}

      <div className="release-editor">
        <div className="stack">
          <div className="card card-pad">
            <label className="field">
              <span className="field-label">Name</span>
              <input
                type="text"
                value={title}
                maxLength={160}
                placeholder="Goldbits portal and Loans — October"
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <div className="field" style={{ maxWidth: 180 }}>
              <span className="field-label">Went live on</span>
              <DateCell value={releasedOn} editable max={today} onChange={(next) => setReleasedOn(next ?? today)} />
            </div>
            <label className="field" style={{ marginBottom: 0 }}>
              <span className="field-label">What this release was for (optional)</span>
              <textarea rows={3} value={summary} maxLength={4000} onChange={(event) => setSummary(event.target.value)} />
            </label>
          </div>

          <div className="card">
            <div className="card-head">
              <h2>What went live</h2>
              <span className="row" style={{ gap: 6, fontSize: 12 }}>
                <span className="dim">Finished between</span>
                <DateCell value={from} editable max={to} onChange={(next) => setFrom(next ?? shift(today, -14))} />
                <span className="dim">and</span>
                <DateCell value={to} editable min={from} max={today} onChange={(next) => setTo(next ?? today)} />
              </span>
            </div>
            <div className="card-pad">
              {!candidates ? (
                <span className="dim">Loading…</span>
              ) : candidates.projects.length === 0 ? (
                <p className="dim" style={{ margin: 0 }}>
                  Nothing was finished in that window. Widen it, or add a project below.
                </p>
              ) : (
                <div className="stack-sm">
                  {candidates.projects.map((entry) => (
                    <div key={entry.project.id} className="release-candidate">
                      <label className="checkbox-row" style={{ marginBottom: 4 }}>
                        <input
                          type="checkbox"
                          checked={has(entry.project.id, null)}
                          onChange={() =>
                            toggle({
                              projectId: entry.project.id,
                              workItemId: null,
                              companyName: entry.company.name,
                              projectName: entry.project.name,
                              workItemKey: '',
                              workItemTitle: '',
                              note: '',
                            })
                          }
                        />
                        <span>
                          <strong>{entry.project.name}</strong>{' '}
                          <span className="dim" style={{ fontSize: 12 }}>
                            {entry.company.name}
                            {entry.project.status === 'delivered' ? ' · delivered' : ''} · the project as a whole
                          </span>
                        </span>
                      </label>
                      {entry.items.map((item) => (
                        <label key={item.id} className="checkbox-row" style={{ marginBottom: 2, paddingLeft: 24 }}>
                          <input
                            type="checkbox"
                            checked={has(entry.project.id, item.id)}
                            onChange={() =>
                              toggle({
                                projectId: entry.project.id,
                                workItemId: item.id,
                                companyName: entry.company.name,
                                projectName: entry.project.name,
                                workItemKey: item.key,
                                workItemTitle: item.title,
                                note: '',
                              })
                            }
                          />
                          <span>
                            <span className="key">{item.key}</span> {item.title}
                            {item.finishedAt && (
                              <span className="dim" style={{ fontSize: 11 }}>
                                {' '}
                                · finished {formatDate(item.finishedAt.slice(0, 10))}
                              </span>
                            )}
                          </span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {anyProjects.length > 0 && (
                <div className="row" style={{ gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
                  <span className="dim" style={{ fontSize: 12 }}>
                    Something else went live?
                  </span>
                  <select
                    value=""
                    style={{ width: 'auto' }}
                    onChange={(event) => {
                      const option = anyProjects.find((entry) => entry.value === event.target.value);
                      if (!option || has(option.value, null)) return;
                      setLines((current) => [
                        ...current,
                        {
                          projectId: option.value,
                          workItemId: null,
                          companyName: companyOf.get(option.value) ?? '',
                          projectName: option.label,
                          workItemKey: '',
                          workItemTitle: '',
                          note: '',
                        },
                      ]);
                    }}
                  >
                    <option value="">Add a project…</option>
                    {anyProjects.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                        {option.hint ? ` (${option.hint})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card release-chosen">
          <div className="card-head">
            <h2>On this release</h2>
            <span className="dim" style={{ fontSize: 12 }}>
              {lines.length} line{lines.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="card-pad">
            {lines.length === 0 ? (
              <p className="dim" style={{ margin: 0 }}>
                Tick projects and tasks on the left. Each can carry a note — what changed, what to look out for.
              </p>
            ) : (
              <div className="stack-sm">
                {lines.map((line, index) => (
                  <div key={`${line.projectId}:${line.workItemId ?? ''}`} className="release-line">
                    <div className="row" style={{ gap: 6, alignItems: 'baseline' }}>
                      <span className="grow">
                        {line.workItemKey ? (
                          <>
                            <span className="key">{line.workItemKey}</span> {line.workItemTitle}
                          </>
                        ) : (
                          <strong>{line.projectName}</strong>
                        )}
                        <span className="dim" style={{ fontSize: 11 }}>
                          {' '}
                          · {line.workItemKey ? line.projectName : line.companyName || 'project'}
                        </span>
                      </span>
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        aria-label="Take it off"
                        onClick={() => setLines((current) => current.filter((_, at) => at !== index))}
                      >
                        ×
                      </button>
                    </div>
                    <input
                      type="text"
                      value={line.note}
                      placeholder="A note (optional)"
                      maxLength={2000}
                      onChange={(event) =>
                        setLines((current) => current.map((entry, at) => (at === index ? { ...entry, note: event.target.value } : entry)))
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
